import { useAuth, useClerk } from "@clerk/clerk-react";
import { CheckCircle2, Zap, Building2, ArrowRight, FileText, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Section } from "@/layouts/Section";

const REPORT_PACKS = [
  { amount: 5,   price: "CA$3.49",  perCredit: "70¢", label: "Quick top-up",  desc: "A few extra students covered" },
  { amount: 30,  price: "CA$11.99", perCredit: "40¢", label: "One full class", desc: "Enough for a typical class of students" },
  { amount: 60,  price: "CA$17.99", perCredit: "30¢", label: "Two classes",    desc: "Great for split-grade or two homerooms" },
  { amount: 120, price: "CA$22.99", perCredit: "19¢", label: "A full year",    desc: "A year's worth of reports at the lowest rate", best: true },
];
const AI_PACKS = [
  { amount: 25,  price: "CA$1.99",  perCredit: "8¢", label: "Light use",     desc: "Selective AI for your trickiest comments" },
  { amount: 100, price: "CA$5.99",  perCredit: "6¢", label: "One full class", desc: "AI comments for every student in a class" },
  { amount: 200, price: "CA$9.99",  perCredit: "5¢", label: "Heavy use",      desc: "AI across multiple classes or report periods" },
  { amount: 500, price: "CA$19.99", perCredit: "4¢", label: "AI-powered",     desc: "Let AI draft every comment, all year long", best: true },
];

function CreditPackRow({ icon: Icon, label, unit, packs }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
        <Icon size={14} className="text-primary" /> {label}
      </h3>
      <div className="grid grid-cols-4 gap-3 max-[600px]:grid-cols-2">
        {packs.map((p) => (
          <div
            key={p.amount}
            className={`relative rounded-xl border bg-card flex flex-col p-4 ${p.best ? "border-primary" : "border-border"}`}
          >
            {p.best && (
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[0.55rem] font-bold tracking-[0.07em] uppercase px-2.5 py-[0.18rem] rounded-full bg-primary text-primary-foreground whitespace-nowrap">
                Best value
              </span>
            )}
            <span className="text-[0.75rem] font-semibold text-foreground mb-2">{p.label}</span>
            <span className="font-display text-[2rem] font-normal leading-none text-foreground">{p.perCredit}</span>
            <span className="text-[0.65rem] text-muted-foreground mt-0.5">per {unit}</span>
            <p className="text-[0.75rem] text-muted-foreground leading-snug mt-2 flex-1">{p.desc}</p>
            <div className="mt-4 pt-3 border-t border-border text-[0.65rem] text-muted-foreground">
              {p.amount} credits · {p.price}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingCard({ icon: Icon, name, price, period, teacherFeatures, adminFeatures = [], highlight, cta }) {
  const dark = highlight;
  return (
    <div className={`rounded-2xl border flex flex-col ${dark ? "bg-brand-900 border-brand-700" : "bg-card border-border"}`}>
      <div className="px-7 pt-7 pb-6">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-4 ${dark ? "bg-brand-700" : "bg-primary/10"}`}>
          <Icon size={18} className={dark ? "text-brand-200" : "text-primary"} />
        </div>
        <h2 className={`font-display text-xl font-normal mb-1 ${dark ? "text-white" : "text-foreground"}`}>{name}</h2>
        <div className="flex items-baseline gap-1.5 mb-1">
          <span className={`font-display text-[2rem] font-normal ${dark ? "text-white" : "text-foreground"}`}>{price}</span>
          {period && (
            <span className={`text-sm ${dark ? "text-brand-300" : "text-muted-foreground"}`}>{period}</span>
          )}
        </div>
      </div>

      <div className={`h-px mx-7 ${dark ? "bg-brand-700" : "bg-border"}`} />

      <div className="px-7 py-6 flex-1 flex flex-col gap-4">
        <div>
          <div className={`text-[0.6rem] font-bold tracking-[0.1em] uppercase mb-2 ${dark ? "text-brand-400" : "text-muted-foreground"}`}>For Teachers</div>
          <ul className="space-y-2.5">
            {teacherFeatures.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm">
                <CheckCircle2 size={15} className={`shrink-0 mt-[1px] ${dark ? "text-brand-400" : "text-primary"}`} />
                <span className={dark ? "text-brand-100" : "text-muted-foreground"}>{f}</span>
              </li>
            ))}
          </ul>
        </div>
        {adminFeatures.length > 0 && (
          <div>
            <div className={`text-[0.6rem] font-bold tracking-[0.1em] uppercase mb-2 ${dark ? "text-brand-400" : "text-muted-foreground"}`}>For Admins</div>
            <ul className="space-y-2.5">
              {adminFeatures.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 size={15} className={`shrink-0 mt-[1px] ${dark ? "text-brand-400" : "text-primary"}`} />
                  <span className={dark ? "text-brand-100" : "text-muted-foreground"}>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="px-7 pb-7">{cta}</div>
    </div>
  );
}

export default function Pricing() {
  const { isSignedIn } = useAuth();
  const { openSignIn } = useClerk();

  const freeCta = isSignedIn ? (
    <Link to="/dashboard">
      <button className="inline-flex items-center justify-center gap-[0.4rem] px-5 py-[0.72rem] bg-transparent text-foreground text-[0.9375rem] font-medium border border-border rounded-lg cursor-pointer transition-[border-color,transform] duration-150 hover:border-foreground hover:-translate-y-px w-full">
        Go to Dashboard <ArrowRight size={14} />
      </button>
    </Link>
  ) : (
    <button
      onClick={() => openSignIn()}
      className="inline-flex items-center justify-center gap-[0.4rem] px-5 py-[0.72rem] bg-transparent text-foreground text-[0.9375rem] font-medium border border-border rounded-lg cursor-pointer transition-[border-color,transform] duration-150 hover:border-foreground hover:-translate-y-px w-full"
    >
      Get started free <ArrowRight size={14} />
    </button>
  );

  const schoolCta = isSignedIn ? (
    <Link to="/schools/seats">
      <button className="inline-flex items-center justify-center gap-[0.4rem] px-5 py-[0.72rem] bg-brand-500 text-white text-[0.9375rem] font-semibold rounded-lg cursor-pointer transition-[background,transform] duration-150 hover:bg-brand-400 hover:-translate-y-px w-full">
        Set up a school <ArrowRight size={14} />
      </button>
    </Link>
  ) : (
    <button
      onClick={() => openSignIn()}
      className="inline-flex items-center justify-center gap-[0.4rem] px-5 py-[0.72rem] bg-brand-500 text-white text-[0.9375rem] font-semibold rounded-lg cursor-pointer transition-[background,transform] duration-150 hover:bg-brand-400 hover:-translate-y-px w-full"
    >
      Get started <ArrowRight size={14} />
    </button>
  );

  return (
    <div className="pricing-page">

      {/* ── Header ──────────────────────────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[640px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-primary mb-3">Plans &amp; Pricing</div>
          <h1 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-foreground m-0 mb-4 leading-tight">
            Simple pricing for every classroom.
          </h1>
          <p className="text-[1.0625rem] leading-[1.65] text-muted-foreground m-0 max-w-[520px]">
            Start free - no credit card required. Upgrade when your school is ready.
          </p>
        </div>
      </Section>

      {/* ── Cards ───────────────────────────────────────────────── */}
      <Section variant="muted">
        <div className="max-w-[800px] mx-auto">
          <div className="grid grid-cols-2 gap-6 items-start max-[640px]:grid-cols-1 max-[640px]:max-w-[420px] max-[640px]:mx-auto">
            <PricingCard
              icon={Zap}
              name="Free"
              price="$0"
              period="forever"
              teacherFeatures={[
                "20 report credits on signup",
                "20 AI generation credits on signup",
                "All report card types (Gr 1–12)",
                "PDF download",
                "Class sharing with view or edit permissions",
                "Bulk export to ZIP",
                "Save report defaults per class",
              ]}
              cta={freeCta}
            />
            <PricingCard
              icon={Building2}
              name="School"
              price="CA$24.99"
              period="/ teacher / year"
              highlight
              teacherFeatures={[
                "120 report credits per teacher / year",
                "200 AI generation credits per teacher / year",
                "All report card types (Gr 1–12)",
                "PDF download",
                "Class sharing with view or edit permissions",
                "Bulk export to ZIP",
                "Save report defaults per class",
              ]}
              adminFeatures={[
                "School admin dashboard",
                "Manage and remove teachers",
                "Assign and manage seats",
                "View all classes across the school",
                "Reassign class ownership between teachers",
                "Per-teacher usage and activity stats",
                "Billing portal + seat management",
                "Controlled sharing mode with approval workflow",
              ]}
              cta={schoolCta}
            />
          </div>
        </div>
      </Section>

      {/* ── Credit Packs ────────────────────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[800px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-primary mb-3">Top up anytime</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-foreground m-0 mb-2 leading-tight">
            Need more credits?
          </h2>
          <p className="text-[1.0625rem] leading-[1.65] text-muted-foreground m-0 mb-8 max-w-[520px]">
            Buy report or AI credits in any amount - they stack with your existing balance.
          </p>
          <div className="flex flex-col gap-6">
            <CreditPackRow icon={FileText} label="Report Credits" unit="report" packs={REPORT_PACKS} />
            <CreditPackRow icon={Sparkles} label="AI Generation Credits" unit="comment" packs={AI_PACKS} />
          </div>
          <p className="text-xs text-muted-foreground mt-5">
            Credits can be purchased from the{" "}
            <Link to="/billing" className="underline hover:text-foreground">Billing page</Link>{" "}
            once signed in.
          </p>
        </div>
      </Section>

      {/* ── FAQ strip ───────────────────────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[900px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-primary mb-3">Common questions</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-foreground m-0 mb-8 leading-tight">
            Good to know before you sign up.
          </h2>
          <div className="grid grid-cols-2 gap-x-12 gap-y-8 max-[680px]:grid-cols-1 max-[680px]:gap-y-7">
            {FAQ.map((item) => (
              <div key={item.q} className="border-t border-border pt-5">
                <h3 className="text-[0.9375rem] font-semibold text-foreground m-0 mb-2">{item.q}</h3>
                <p className="text-[0.9rem] leading-[1.65] text-muted-foreground m-0">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── Bottom CTA ──────────────────────────────────────────── */}
      <Section variant="dark">
        <div className="max-w-[1100px] mx-auto text-center">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-brand-300 mb-3">Get started today</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-white m-0 mb-12 leading-tight">
            Try it free - no account needed.
          </h2>
          <div className="flex flex-wrap gap-3 items-center justify-center">
            <Link to="/pgrc">
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-[0.9375rem] font-semibold rounded-lg cursor-pointer transition-[background,transform] duration-150 hover:bg-primary-hover hover:-translate-y-px no-underline whitespace-nowrap">
                Try a free report card <ArrowRight size={16} />
              </button>
            </Link>
          </div>
        </div>
      </Section>

    </div>
  );
}

const FAQ = [
  {
    q: "What counts as a 'report credit'?",
    a: "Each new student report you create uses one credit. Editing and re-saving an existing report never costs an additional credit - only creation does.",
  },
  {
    q: "What are AI credits used for?",
    a: "AI credits can be spent on all AI Tools. For example, when you use the AI comment generator to write a personalized comment for a student. One credit equals one generated comment. They are separate from report credits and can be purchased independently.",
  },
  {
    q: "How does the free plan work?",
    a: "When you sign up, you receive 20 report credits and 20 AI credits - no credit card required, no expiry. Once you run out, you can top up with credit packs or upgrade to a School plan.",
  },
  {
    q: "Can I share a class with a colleague?",
    a: "Yes. You can share any class with any EasyReports user and grant view or edit permissions. Shared classes only count against the owner's quota. Schools using restricted-sharing mode require admin approval before a share goes through.",
  },
  {
    q: "What happens when my school subscription lapses?",
    a: "Your subscription renews annually on the same date each year. If it lapses, all data stays intact - teachers just fall back to their personal credit wallet or can top up with credit packs until the school renews.",
  },
  {
    q: "What report card types are supported?",
    a: "All five Ontario Ministry of Education formats are available: Progress Reports (Gr 1–6 and Gr 7–8) and Provincial Report Cards (Gr 1–6, Gr 7–8, and Gr 9–12 semester). More formats are added based on teacher feedback.",
  },
];
